import React from 'react';
import { NewButton } from '../NewButton';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { Menu, MenuItem, ListItemIcon, ListItemText, Breakpoint, Dialog, DialogContent, MenuList, DialogActions, Button } from '@mui/material';
import { useTranslation } from 'react-i18next';

export interface ActionItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  onClick: () => void | Promise<void>;
  disabled?: boolean;
}

export interface ActionsButtonProps {
  actions: ActionItem[];
  buttonText?: string;
  buttonIcon?: React.ReactNode;
  breakpoint?: Breakpoint;
  fixedPosition?: boolean;
  color?: any;
  sx?: React.CSSProperties;
  useDropdown?: boolean; // If true, use dropdown; if false, use dialog
}

function ActionsButton(props: ActionsButtonProps) {
  const [ anchorEl, setAnchorEl ] = React.useState<null | HTMLElement>(null);
  const [ dialogOpen, setDialogOpen ] = React.useState<boolean>(false);
  const dropdownOpen = Boolean(anchorEl);
  const { t } = useTranslation();

  const handleButtonClick = (event?: React.MouseEvent<HTMLButtonElement>) => {
    if (props.useDropdown && event) {
      setAnchorEl(event.currentTarget);
    } else {
      setDialogOpen(true);
    }
  };

  const handleDropdownClose = () => {
    setAnchorEl(null);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  const handleActionClick = async (action: ActionItem) => {
    if (props.useDropdown) {
      handleDropdownClose();
    } else {
      handleDialogClose();
    }
    await action.onClick();
  };

  return (
    <React.Fragment>
      <NewButton
        text={props.buttonText ?? t('analysis.button_other')}
        icon={props.buttonIcon ?? <MoreVertIcon />}
        fixedPosition={props.fixedPosition ?? false}
        breakpoint={props.breakpoint ?? 'lg'}
        color={props.color}
        sx={props.sx}
        onClick={handleButtonClick}
      />
      {props.useDropdown && (
        <Menu
          anchorEl={anchorEl}
          open={dropdownOpen}
          onClose={handleDropdownClose}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          slotProps={{
            paper: {
              sx: {
                maxHeight: 400,
              },
            },
          }}
        >
          {props.actions.map((action) => (
            <MenuItem 
              key={action.id} 
              onClick={() => handleActionClick(action)}
              disabled={action.disabled}
            >
              <ListItemIcon>
                {action.icon}
              </ListItemIcon>
              <ListItemText primary={t(action.label)} />
            </MenuItem>
          ))}
        </Menu>
      )}
      {!props.useDropdown && (
        <Dialog
          fullWidth
          maxWidth="sm"
          open={dialogOpen}
          onClose={handleDialogClose}
        >
          <DialogContent style={{
            margin: 0,
            paddingLeft: 10,
            paddingRight: 10,
          }}>
            <MenuList>
              {props.actions.map((action) => (
                <MenuItem 
                  key={action.id} 
                  onClick={() => handleActionClick(action)}
                  disabled={action.disabled}
                >
                  <ListItemIcon>
                    {action.icon}
                  </ListItemIcon>
                  <ListItemText primary={t(action.label)} />
                </MenuItem>
              ))}
            </MenuList>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDialogClose}>{t('Cancel')}</Button>
          </DialogActions>
        </Dialog>
      )}
    </React.Fragment>
  );
}

export default ActionsButton;
